import {PromoAction} from '../../../../services/cellcast.v2.types'
import {CardViewType} from '../../../Utils/Constant/WalletCard'
import {
  WALLET_CARD_FORM_SETTINGS,
  FIELD_PLACEHOLDER_TO_KEY_MAP,
} from '../../../Utils/Constant/WalletCard'

export enum CardType {
  WALLET = 'WALLET',
  CARD = 'CARD',
  BUSINESS_CARD = 'Business Cards',
}

export enum ShareMethod {
  WALLET = 'wallet',
  CONTACT = 'contact',
}

export enum ViewType {
  WALLET_CARD = 'wallet',
  CONTACT = 'contact',
}

export interface Card {
  id: string
  type: CardType
  cardName: string
  createdAt: Date
  updatedAt: Date
}

export interface Pagination {
  page: number
  limit: number
  total: number
}

export interface ApiWalletCardsResponse {
  items: CardItem[]
  total: number
  totalPages: number
  current: number
}
export interface CardItem {
  id: string
  type: string
  cardName: string
  createdAt: string
  updatedAt: string
  [key: string]: any
}
/**
 * Interface for card data used across wallet card components
 */
export interface CardData {
  id?: string
  type: string
  organizationName?: string
  cardName: string
  cardLabel: string
  fullName: string
  jobTitle?: string
  isDefault?: boolean
  companyName?: string
  address?: string
  phone: string
  email: string
  website?: string
  shareMethod: ShareMethod[]
  logo?: string
  logoGoogle?: string
  picture?: string
  pictureGoogle?: string
  backgroundImage?: string
  user: string
  backgroundColor: string
  foregroundColor: string
  labelColor: string
  imagePreviewUrl?: string
  promoSettings: PromoAction[]
  formSettings: FormSetting[]
  formBackgroundColor: string
  formLabelColor: string
  contactIconColor: string
  template?: string | null
  status?: string
  sender?: string
  senderType?: string
  delayMinutes?: number
  aiWelcomeMessage?: boolean
  vehicleTypeOptions?: string[]
  walletCardTemplate?: string
}

export interface CardDataFormSubmit {
  id?: string
  type: string
  organizationName?: string
  cardLabel: string
  cardName: string
  isDefault: boolean
  fullName: string
  jobTitle?: string
  companyName?: string
  address?: string
  phone: string
  email: string
  website?: string
  shareMethod: ShareMethod[]
  logo?: string
  picture?: string
  pictureGoogle?: string
  backgroundImage?: string
  user: string
  backgroundColor: string
  foregroundColor: string
  labelColor: string
  imagePreviewUrl?: string
  promoSettings?: string[]
  formSettings?: FormSettingAPI[]
  formBackgroundColor: string
  formLabelColor: string
  contactIconColor: string
  template?: string | null
  walletCardTemplate: string
}
/**
 * Interface for location state used in routing
 */
export interface LocationState {
  isEdit?: boolean
  data?: CardData
}

/**
 * Props for FormWalletCard component
 */
export interface FormWalletCardProps {
  initialData?: CardData
  isEdit?: boolean
  onDataChange?: (data: Partial<CardData>) => void
}

/**
 * Props for PreviewWalletCard component
 */
export interface PreviewWalletCardProps {
  cardData: CardData
  onCardTypeChange?: (type: string) => void
}

/**
 * Props for ImageUploader component
 */
export interface ImageUploaderProps {
  label: string
  type?: string
  currentImage?: string | null
  onImageSelected?: (appleUrl: string | null, googleUrl?: string | null) => void
  onUploadStart?: () => void
  onUploadEnd?: () => void
}

/**
 * Props for StepperNavigation component
 */
export interface StepperNavigationProps {
  currentStep: number
  steps: {
    number: number
    title: string
  }[]
}

export type SuccessPageProps = {
  dataShareContact: DataShareContact
  name: string
  onClose?: () => void
}

export interface PreviewWalletCardProps {
  cardData: CardData
  onCardTypeChange?: (type: string) => void
  onCardDataChange?: (data: Partial<CardData>) => void
}

export interface PreviewWalletCardGoogleProps {
  cardData: CardData
  customStyles?: React.CSSProperties
}

export interface ExtendedPreviewWalletCardProps extends PreviewWalletCardProps {
  onViewChange?: (view: CardViewType) => void
  disableInput?: boolean
  promoSettingIds?: string[]
  formFields?: FormSetting[]
  promoActionLabels?: string[]
  expandedStep?: string
  setExpandedStep?: (step: string) => void
  /**
   * When true, disables the step synchronization logic between the preview component
   * and the wizard stepper. Useful for screens that only need the preview without
   * affecting or reacting to the current step (e.g., success/detail pages).
   */
  disableStepSync?: boolean
  currentView?: CardViewType
  setCurrentView?: (view: CardViewType) => void
}

// Backend FormSetting interface
export interface FormSetting {
  _id: string
  label: string
  key: string
  color: string
  isRequired: boolean
  order: number
  isDisplay?: boolean
}

// FormSetting for API submission (without _id field)
export interface FormSettingAPI {
  color: string
  isRequired: boolean
  order: number
  label: string
  key: string
  isDisplay?: boolean
}

// Legacy FormField interface for backwards compatibility
export interface FormField {
  id: number
  label: string
  key: string
  required: boolean
}

// Utility function to transform FormField[] to FormSetting[]
export const transformFormFieldsToFormSettings = (formFields: FormField[]): FormSetting[] => {
  return formFields.map((field, index) => ({
    label: field.label,
    key: FIELD_PLACEHOLDER_TO_KEY_MAP[field.key],
    color: '', // Default color, can be customized later
    isRequired: field.required,
    order: index + 1, // 1-based ordering
    _id: `generated_${Date.now()}_${index}`, // Generate temporary ID
  }))
}

// Utility function to transform FormSetting[] to FormField[] for backwards compatibility
export const transformFormSettingsToFormFields = (formSettings: FormSetting[]): FormField[] => {
  return formSettings
    .sort((a, b) => a.order - b.order) // Sort by order
    .map((setting, index) => ({
      id: index + 1,
      label: setting.label,
      key: setting.key,
      required: setting.isRequired,
    }))
}

// Default formSettings if none provided
export const getDefaultFormSettings = (): FormSetting[] => [
  {
    color: '',
    isRequired: true,
    order: 1,
    _id: 'default_fullname',
    label: 'Full Name',
    key: 'fullName',
  },
  {
    color: '',
    isRequired: false,
    order: 2,
    _id: 'default_phone',
    label: 'Phone',
    key: 'phone',
  },
  {
    color: '',
    isRequired: false,
    order: 3,
    _id: 'default_email',
    label: 'Email',
    key: 'email',
  },
  {
    color: '',
    isRequired: false,
    order: 4,
    _id: 'default_company',
    label: 'Company Name',
    key: 'companyName',
  },
]

// Utility function to validate and sort formSettings
export const processFormSettings = (formSettings: FormSetting[]): FormSetting[] => {
  if (!formSettings || formSettings.length === 0) {
    return getDefaultFormSettings()
  }

  // Sort by order and ensure all required fields exist
  return formSettings
    .filter((setting) => setting._id && setting.key && setting.label)
    .sort((a, b) => a.order - b.order)
}

// Utility function to convert FormSetting to FormSettingAPI (remove _id field)
export const formSettingToAPI = (formSettings: FormSetting[]): FormSettingAPI[] => {
  return formSettings.map(({_id, ...setting}) => setting)
}

// Interface for the complete form connect settings data to send to backend
export interface FormConnectSettingsData {
  promoSettings: string[] // Array of promo action IDs
  formSettings: FormSetting[] // Array of form field settings
}

export interface ExtendedFormWalletCardProps extends FormWalletCardProps {
  isTemplate?: boolean
  hideJobTitle?: boolean
  setCompletedSteps?: (steps: Record<string, boolean>) => void
  expandedStep?: string
  onStepExpand?: (step: string) => void
  promoSettingIds?: string[]
  setPromoSettingIds?: (ids: string[]) => void
  vehicleTypeOptionIds?: string[]
  setVehicleTypeOptionIds?: (ids: string[]) => void
  formFields?: FormSetting[]
  setFormFields?: (fields: FormSetting[]) => void
  promoActionList?: (labels: string[]) => void
  setWalletCardTemplateID: (id: string) => void
  walletCardTemplateID: string
  templateOptions: any[]
  visitedSteps?: Record<string, boolean>
  viewType?: CardViewType
}

/**
 * Type for required fields in wallet card form validation
 */
export type RequiredFormFields = [
  'cardName',
  'organizationName',
  'fullName',
  'phone',
  'email',
  'shareMethod',
  'website'
]

export type FormWalletCardPublicProps = {
  name: string
  userID: string
  picture: string
  onSuccess?: () => void
  isInPopup?: boolean
  source: string
  targetID?: string
  setShowPopup: (show: boolean) => void
  setDataShareContact: (data: DataShareContact) => void
  promoSettings: PromoAction[]
  formSettings?: FormSetting[]
  formBackgroundColor: string
  formLabelColor: string
  contactIconColor: string
  vehicleTypeOptions?: string[]
}

// Array of fields required for wallet card validation
export const FormWalletCardFields = [
  'cardName',
  'organizationName',
  'fullName',
  'phone',
  'email',
  'shareMethod',
  'website',
] as const
export type FormWalletCardField = typeof FormWalletCardFields[number]

export const FormWalletCardPublicFields = ['fullName', 'phone', 'email', 'website'] as const

export interface DataShareContact {
  fullName: string
  phone?: string
  email?: string
  website?: string
  address?: string
  jobTitle?: string
  companyName?: string
  interestedVehicleType?: string
} // TODO: add more fields

export interface ShareOptionsProps {
  cardData: CardData
  handleChange: (e: any) => void
  errors: Record<string, string>
  isLoading: boolean
  handleBlur: (e: any) => void
  touched: Record<string, boolean>
  isExpanded?: boolean
  onExpand?: (step: string) => void
  isCompleted?: boolean
  setIsCompleted?: (completed: boolean) => void
  gateUntilOpened?: boolean
}

export interface SettingFormConnectProps {
  cardData: CardData
  handleChange: (e: any) => void
  handleBlur: (e: any) => void
  touched: Record<string, boolean>
  errors: Record<string, string>
  MAX_LENGTH: number
  disableInput: boolean
  isExpanded?: boolean
  onExpand?: () => void
  isCompleted?: boolean
  setIsCompleted?: (completed: boolean) => void
  isLoading?: boolean
  promoSettingIds?: string[]
  setPromoSettingIds?: (ids: string[]) => void
  vehicleTypeOptionIds?: string[]
  setVehicleTypeOptionIds?: (ids: string[]) => void
  formFields?: FormSetting[]
  setFormFields?: (fields: FormSetting[]) => void
  promoActionList?: (labels: string[]) => void
  onFormConnectDataChange?: (data: FormConnectSettingsData) => void
  gateUntilOpened?: boolean
}
